import type { Dispatch } from '@reduxjs/toolkit';

import { type DispatchContext, pumpSseReader, subscribe } from '../sse';
import { flushActionTurn } from '../sse/actionTurn';
import { SidekickHttpError } from '../sse/sidekickHttp';
import { setIsReconnecting } from '../state/aiSidekickChatSlice';
import type { SidekickError } from '../state/chatTypes';
import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';

type ActiveStream = {
  reader: ReadableStreamDefaultReader<Uint8Array>;
  initialChunk?: Uint8Array;
};

type ReopenStreamResult =
  | { status: 'opened' }
  | { status: 'exhausted' }
  | { status: 'superseded' }
  | { status: 'failed'; error: unknown };

type DrainSidekickEventStreamParams = {
  apiBaseUrl: string;
  conversationId: string;
  controller: AbortController;
  activeStream: ActiveStream;
  ctx: DispatchContext;
  dispatch: Dispatch;
  clearPartialStreamState: () => void;
  onError: (error: SidekickError) => void;
};

const toTransportError = (err: unknown): SidekickError => {
  if (err instanceof SidekickHttpError) {
    return err.sidekickError;
  }
  return {
    code: SYNTHETIC_TRANSPORT_ERROR_CODE,
    message: err instanceof Error ? err.message : String(err),
  };
};

/**
 * Drains a single Sidekick SSE turn, including the one bounded reconnect
 * allowed for recoverable reader failures, backend reconnect frames, and
 * pre-`done` stream closure.
 */
export const drainSidekickEventStream = async (
  params: DrainSidekickEventStreamParams
): Promise<void> => {
  const {
    apiBaseUrl,
    conversationId,
    controller,
    activeStream,
    ctx,
    dispatch,
    clearPartialStreamState,
    onError,
  } = params;
  let activeReader = activeStream.reader;
  let activeInitialChunk = activeStream.initialChunk;
  let midStreamRetried = false;
  let clearReconnectOnNextEvent = false;
  let doneSeen = false;

  const isActive = (): boolean =>
    !controller.signal.aborted && ctx.isActive?.() !== false;

  const reportTransportError = (err: unknown): void => {
    if (isActive()) {
      onError(toTransportError(err));
    }
  };

  const cancelActiveReader = async (): Promise<void> => {
    await activeReader.cancel().catch(() => undefined);
  };

  const reopenStreamOnce = async (): Promise<ReopenStreamResult> => {
    if (!isActive()) {
      return { status: 'superseded' };
    }
    if (midStreamRetried) {
      return { status: 'exhausted' };
    }
    midStreamRetried = true;
    dispatch(setIsReconnecting(true));
    clearReconnectOnNextEvent = true;
    try {
      await cancelActiveReader();
      if (!isActive()) {
        return { status: 'superseded' };
      }
      const reopened = await subscribe({
        apiBaseUrl,
        conversationId,
        lastEventId: ctx.lastEventIdRef.current,
        signal: controller.signal,
      });
      if (!isActive()) {
        await reopened.reader.cancel().catch(() => undefined);
        return { status: 'superseded' };
      }
      activeReader = reopened.reader;
      activeInitialChunk = reopened.initialChunk;
      return { status: 'opened' };
    } catch (reopenErr) {
      if (!isActive()) {
        return { status: 'superseded' };
      }
      return { status: 'failed', error: reopenErr };
    }
  };

  // Actions the backend already ran stay on the turn's answer even when
  // the stream fails before `done`.
  const failTurn = (err: unknown): void => {
    if (isActive()) {
      flushActionTurn(ctx.actionTurnRef, dispatch);
    }
    clearPartialStreamState();
    reportTransportError(err);
  };

  const failAfterRecoveryExhausted = (message: string): void => {
    failTurn(
      new SidekickHttpError(0, {
        code: SYNTHETIC_TRANSPORT_ERROR_CODE,
        message,
      })
    );
  };

  while (true) {
    if (!isActive()) {
      return;
    }
    try {
      const result = await pumpSseReader({
        reader: activeReader,
        initialChunk: activeInitialChunk,
        ctx,
        signal: controller.signal,
        onDecodedEvent: () => {
          if (isActive() && clearReconnectOnNextEvent) {
            clearReconnectOnNextEvent = false;
            dispatch(setIsReconnecting(false));
          }
        },
      });
      activeInitialChunk = undefined;
      if (!isActive()) {
        return;
      }
      if (result === 'done') {
        doneSeen = true;
        return;
      }
      if (result === 'error') {
        return;
      }
      if (result === 'reconnect') {
        const reopenResult = await reopenStreamOnce();
        if (!isActive() || reopenResult.status === 'superseded') {
          return;
        }
        if (reopenResult.status === 'opened') {
          continue;
        }
        if (reopenResult.status === 'failed') {
          failTurn(reopenResult.error);
        } else {
          failAfterRecoveryExhausted(
            'Sidekick SSE: reconnect requested but recovery failed'
          );
        }
        return;
      }
      if (result === 'closed') {
        if (doneSeen || !isActive()) {
          return;
        }
        const reopenResult = await reopenStreamOnce();
        if (!isActive() || reopenResult.status === 'superseded') {
          return;
        }
        if (reopenResult.status === 'opened') {
          continue;
        }
        if (reopenResult.status === 'failed') {
          failTurn(reopenResult.error);
        } else {
          failAfterRecoveryExhausted('Sidekick SSE: stream closed before done');
        }
        return;
      }
    } catch (err) {
      if (!isActive()) {
        return;
      }
      if (midStreamRetried) {
        await cancelActiveReader();
        if (!isActive()) {
          return;
        }
        failTurn(err);
        return;
      }
      const reopenResult = await reopenStreamOnce();
      if (!isActive() || reopenResult.status === 'superseded') {
        return;
      }
      if (reopenResult.status === 'opened') {
        continue;
      }
      if (reopenResult.status === 'failed') {
        failTurn(reopenResult.error);
      }
      return;
    }
  }
};
